import { apiClient } from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';
import type {
  PatientRegistrationApprovalActionRequest,
  PatientRegistrationApprovalActionResult,
  PatientRegistrationApprovalDetail,
  PatientRegistrationApprovalListQuery,
  PatientRegistrationApprovalPagedResult,
  PatientRegistrationApprovalPendingCount,
  ProgramRegistrationApprovalConfiguration,
  SaveProgramRegistrationApprovalConfiguration,
} from '../types/patientRegistrationApproval';

const base = (programId: string) => `/programs/${programId}/patient-registration-approvals`;
const configBase = (programId: string) => `/programs/${programId}/registration-approval-config`;

export const patientRegistrationApprovalService = {
  async getApprovals(
    programId: string,
    query: PatientRegistrationApprovalListQuery,
  ): Promise<ApiResponse<PatientRegistrationApprovalPagedResult>> {
    const response = await apiClient.get<ApiResponse<PatientRegistrationApprovalPagedResult>>(base(programId), {
      params: query,
    });

    return response.data;
  },

  async getApproval(
    programId: string,
    approvalId: string,
  ): Promise<ApiResponse<PatientRegistrationApprovalDetail>> {
    const response = await apiClient.get<ApiResponse<PatientRegistrationApprovalDetail>>(
      `${base(programId)}/${approvalId}`,
    );

    return response.data;
  },

  async approve(
    programId: string,
    approvalId: string,
    request: PatientRegistrationApprovalActionRequest,
  ): Promise<ApiResponse<PatientRegistrationApprovalActionResult>> {
    const response = await apiClient.post<ApiResponse<PatientRegistrationApprovalActionResult>>(
      `${base(programId)}/${approvalId}/approve`,
      request,
    );

    return response.data;
  },

  async reject(
    programId: string,
    approvalId: string,
    request: PatientRegistrationApprovalActionRequest,
  ): Promise<ApiResponse<PatientRegistrationApprovalActionResult>> {
    const response = await apiClient.post<ApiResponse<PatientRegistrationApprovalActionResult>>(
      `${base(programId)}/${approvalId}/reject`,
      request,
    );

    return response.data;
  },

  async getConfiguration(programId: string): Promise<ApiResponse<ProgramRegistrationApprovalConfiguration>> {
    const response = await apiClient.get<ApiResponse<ProgramRegistrationApprovalConfiguration>>(
      configBase(programId),
    );

    return response.data;
  },

  async saveConfiguration(
    programId: string,
    request: SaveProgramRegistrationApprovalConfiguration,
  ): Promise<ApiResponse<ProgramRegistrationApprovalConfiguration>> {
    const response = await apiClient.put<ApiResponse<ProgramRegistrationApprovalConfiguration>>(
      configBase(programId),
      request,
    );

    return response.data;
  },

  async getPendingCount(programId: string): Promise<ApiResponse<PatientRegistrationApprovalPendingCount>> {
    const response = await apiClient.get<ApiResponse<PatientRegistrationApprovalPendingCount>>(
      `${base(programId)}/pending-count`,
    );

    return response.data;
  },
};
